// src/admin/services/returnRequestService.ts
import {
  collection,
  doc,
  updateDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
  serverTimestamp,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ReturnReason =
  | 'wrong_item'
  | 'defective'
  | 'damaged'
  | 'not_as_described'
  | 'size_issue'
  | 'changed_mind'
  | 'late_delivery'
  | 'other';

export interface ReturnItem {
  productId: string;
  productName: string;
  productSku?: string;
  productImage?: string;
  variantId?: string;
  variantSelections?: Array<{ variantType: string; variantValue: string }>;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export type ReturnStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'completed';

/**
 * Refund lifecycle — kept in sync with PaymentStatus in src/types/Order.ts:
 *   pending            – no refund action yet
 *   partially_refunded – only part of refundAmount was returned
 *   refunded           – full amount returned (maps to PaymentStatus 'refunded')
 */
export type RefundStatus =
  | 'pending'
  | 'partially_refunded'
  | 'refunded';

export type RefundMethod =
  | 'original_payment'
  | 'bank_transfer'
  | 'store_credit'
  | 'cash'
  | 'cheque'
  | 'other';

export const REFUND_STATUS_LABELS: Record<RefundStatus, string> = {
  pending: 'Pending',
  partially_refunded: 'Partially Refunded',
  refunded: 'Fully Refunded',
};

export const REFUND_METHOD_LABELS: Record<RefundMethod, string> = {
  original_payment: 'Original Payment Method',
  bank_transfer: 'Bank Transfer',
  store_credit: 'Store Credit',
  cash: 'Cash',
  cheque: 'Cheque',
  other: 'Other',
};

export interface ReturnRequest {
  id?: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  items: ReturnItem[];
  reason: ReturnReason;
  damageNotes?: string;
  additionalNotes?: string;
  status: ReturnStatus;
  refundAmount: number;          // total amount eligible for refund
  // ── Refund payment tracking ───────────────────────────────────────────────
  refundStatus?: RefundStatus;   // payment refund lifecycle (synced to order paymentStatus)
  refundedAmount?: number;       // actual money returned (partial support)
  refundMethod?: RefundMethod;
  refundTransactionId?: string;
  refundDate?: Date;
  refundNotes?: string;
  // ─────────────────────────────────────────────────────────────────────────
  createdAt?: Date;
  updatedAt?: Date;
  adminNotes?: string;
}

export const RETURN_REASON_LABELS: Record<ReturnReason, string> = {
  wrong_item: 'Wrong Item Received',
  defective: 'Defective Product',
  damaged: 'Arrived Damaged',
  not_as_described: 'Not as Described',
  size_issue: 'Size / Fit Issue',
  changed_mind: 'Changed My Mind',
  late_delivery: 'Arrived Too Late',
  other: 'Other Reason',
};

export const RETURN_STATUS_LABELS: Record<ReturnStatus, string> = {
  pending: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
  completed: 'Completed',
};

const RETURN_REQUESTS_COLLECTION = 'return_requests';

export interface PaginatedReturnRequestsResult {
  requests: ReturnRequest[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface StatusCounts {
  pending: number;
  approved: number;
  rejected: number;
  completed: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toDate = (ts: any): Date | undefined => {
  if (!ts) return undefined;
  if (ts.toDate) return ts.toDate();
  if (ts instanceof Date) return ts;
  if (ts.seconds !== undefined) return new Date(ts.seconds * 1000);
  return undefined;
};

/**
 * Normalise a stored refundStatus value to the new 3-value enum.
 * Old values ('processing', 'not_refunded') are migrated on read so the UI
 * never receives an unknown value.
 */
const normaliseRefundStatus = (raw: string | undefined): RefundStatus | undefined => {
  if (!raw) return undefined;
  if (raw === 'processing') return 'pending';       // legacy → pending
  if (raw === 'not_refunded') return 'pending';     // legacy → pending
  if (['pending', 'partially_refunded', 'refunded'].includes(raw)) {
    return raw as RefundStatus;
  }
  return undefined;
};

const mapDocToRequest = (d: QueryDocumentSnapshot<DocumentData>): ReturnRequest => {
  const data = d.data();
  return {
    id: d.id,
    orderId: data.orderId,
    orderNumber: data.orderNumber,
    customerName: data.customerName,
    customerEmail: data.customerEmail,
    items: data.items || [],
    reason: data.reason,
    damageNotes: data.damageNotes || undefined,
    additionalNotes: data.additionalNotes || undefined,
    status: data.status,
    refundAmount: data.refundAmount || 0,
    refundStatus: normaliseRefundStatus(data.refundStatus),
    refundedAmount: data.refundedAmount ?? undefined,
    refundMethod: data.refundMethod || undefined,
    refundTransactionId: data.refundTransactionId || undefined,
    refundDate: toDate(data.refundDate),
    refundNotes: data.refundNotes || undefined,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    adminNotes: data.adminNotes || undefined,
  };
};

// ─── Service ──────────────────────────────────────────────────────────────────

export class ReturnRequestService {

  /**
   * Fetch the document count for each ReturnStatus in a single round of
   * parallel getCountFromServer calls.
   */
  static async getStatusCounts(): Promise<StatusCounts> {
    const collRef = collection(db, RETURN_REQUESTS_COLLECTION);

    const statuses: ReturnStatus[] = ['pending', 'approved', 'rejected', 'completed'];

    const counts = await Promise.all(
      statuses.map(status =>
        getCountFromServer(query(collRef, where('status', '==', status)))
          .then(snap => snap.data().count)
      )
    );

    return {
      pending: counts[0],
      approved: counts[1],
      rejected: counts[2],
      completed: counts[3],
    };
  }

  /**
   * Fetch paginated return requests with optional filters.
   *
   * FIX: Replaced cursor-based pagination (startAfter / endBefore /
   * limitToLast) with a two-step cursor-seek so any page can be fetched
   * in any order (page 1 → page 5 → page 2 all work correctly).
   *
   * HOW IT WORKS
   * ─────────────
   * Firestore's client SDK has no offset(). The classic startAfter cursor
   * only works sequentially. To jump to page N we:
   *
   *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
   *             seek). The last doc from this set becomes our startAfter cursor.
   *   Step 2 — fetch the real pageSize docs starting after that cursor.
   *
   * For page 1 the seek is skipped entirely (skip count = 0).
   */
  static async getReturnRequestsPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string,
    statusFilter?: ReturnStatus | 'all',
    reasonFilter?: string
  ): Promise<PaginatedReturnRequestsResult> {
    const collRef = collection(db, RETURN_REQUESTS_COLLECTION);

    // Build where-constraints (shared between count + seek + page queries)
    const whereConstraints: ReturnType<typeof where>[] = [];
    if (statusFilter && statusFilter !== 'all') {
      whereConstraints.push(where('status', '==', statusFilter));
    }
    if (reasonFilter && reasonFilter !== 'all') {
      whereConstraints.push(where('reason', '==', reasonFilter));
    }

    const orderConstraint = orderBy('createdAt', 'desc');

    // ── Total count (0 doc reads) ──────────────────────────────────────────
    const countSnap = await getCountFromServer(
      query(collRef, ...whereConstraints)
    );
    const totalCount = countSnap.data().count;

    // ── Step 1: cursor seek ────────────────────────────────────────────────
    let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
    const skipCount = (page - 1) * pageSize;

    if (skipCount > 0) {
      const seekSnapshot = await getDocs(
        query(collRef, ...whereConstraints, orderConstraint, limit(skipCount))
      );
      if (!seekSnapshot.empty) {
        cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
      }
    }

    // ── Step 2: fetch the real page ────────────────────────────────────────
    const pageQuery: Query<DocumentData> = cursorDoc
      ? query(collRef, ...whereConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
      : query(collRef, ...whereConstraints, orderConstraint, limit(pageSize));

    const snap = await getDocs(pageQuery);
    let requests = snap.docs.map(mapDocToRequest);

    // ── Client-side search filter ─────────────────────────────────────────
    if (searchQuery && searchQuery.trim()) {
      const s = searchQuery.toLowerCase();
      requests = requests.filter(
        r =>
          r.orderNumber.toLowerCase().includes(s) ||
          r.customerName.toLowerCase().includes(s) ||
          r.customerEmail.toLowerCase().includes(s)
      );
    }

    return {
      requests,
      totalCount,
      lastVisible: null,  // no longer used by callers
      firstVisible: null,  // no longer used by callers
    };
  }

  /** Approve a return request. */
  static async approveReturnRequest(requestId: string, adminNotes?: string): Promise<void> {
    const ref = doc(db, RETURN_REQUESTS_COLLECTION, requestId);
    await updateDoc(ref, {
      status: 'approved' as ReturnStatus,
      adminNotes: adminNotes || null,
      updatedAt: serverTimestamp(),
    });
  }

  /** Reject a return request. */
  static async rejectReturnRequest(requestId: string, adminNotes?: string): Promise<void> {
    const ref = doc(db, RETURN_REQUESTS_COLLECTION, requestId);
    await updateDoc(ref, {
      status: 'rejected' as ReturnStatus,
      adminNotes: adminNotes || null,
      updatedAt: serverTimestamp(),
    });
  }

  /** Mark as completed (stock restored). */
  static async completeReturnRequest(requestId: string, adminNotes?: string): Promise<void> {
    const ref = doc(db, RETURN_REQUESTS_COLLECTION, requestId);
    await updateDoc(ref, {
      status: 'completed' as ReturnStatus,
      adminNotes: adminNotes || null,
      updatedAt: serverTimestamp(),
    });
  }

  /** Update admin notes without changing status. */
  static async updateAdminNotes(requestId: string, adminNotes: string): Promise<void> {
    const ref = doc(db, RETURN_REQUESTS_COLLECTION, requestId);
    await updateDoc(ref, {
      adminNotes,
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Save refund payment details for a return request.
   * Supports full and partial refunds.
   */
  static async updateRefundDetails(
    requestId: string,
    details: {
      refundStatus: RefundStatus;
      refundedAmount: number;
      refundMethod: RefundMethod;
      refundTransactionId?: string;
      refundDate?: Date;
      refundNotes?: string;
    }
  ): Promise<void> {
    const ref = doc(db, RETURN_REQUESTS_COLLECTION, requestId);
    await updateDoc(ref, {
      refundStatus: details.refundStatus,
      refundedAmount: details.refundedAmount,
      refundMethod: details.refundMethod,
      refundTransactionId: details.refundTransactionId || null,
      refundDate: details.refundDate ?? null,
      refundNotes: details.refundNotes || null,
      updatedAt: serverTimestamp(),
    });
  }

  /** Get a single return request by ID. */
  static async getReturnRequestById(requestId: string): Promise<ReturnRequest | null> {
    const ref = doc(db, RETURN_REQUESTS_COLLECTION, requestId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return mapDocToRequest(snap as QueryDocumentSnapshot<DocumentData>);
  }

  /** Permanently delete a return request document. */
  static async deleteReturnRequest(
    requestId: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const ref = doc(db, RETURN_REQUESTS_COLLECTION, requestId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        return { success: false, message: 'Return request not found.' };
      }
      await deleteDoc(ref);
      return { success: true, message: 'Return request deleted successfully!' };
    } catch (error) {
      console.error('Error deleting return request:', error);
      return { success: false, message: 'Failed to delete return request. Please try again.' };
    }
  }
}